import axios from 'axios';

import apiClient from 'api/apiClient';
import { backend_url as backendUrl } from './config/variables';

// ============================================================================
// DATE PROCESSING UTILITIES
// ============================================================================

/**
 * ISO datetime pattern - matches strings like:
 * - "2024-01-15T10:30:00Z"
 * - "2024-01-15T10:30:00.123Z"
 * - "2024-01-15T10:30:00+00:00"
 * - "2024-01-15T10:30:00" (no timezone - will be treated as UTC)
 */
const ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

/**
 * ISO date-only pattern - matches strings like:
 * - "2024-01-15"
 * Does NOT match datetime strings (which have T and time component)
 */
const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Check if a string value looks like an ISO datetime (with time component)
 */
const isISODateTimeString = (value) => {
  if (typeof value !== 'string') return false;
  return ISO_DATETIME_PATTERN.test(value);
};

/**
 * Check if a string value looks like an ISO date-only string (YYYY-MM-DD)
 */
const isISODateOnlyString = (value) => {
  if (typeof value !== 'string') return false;
  return ISO_DATE_ONLY_PATTERN.test(value);
};

/**
 * Format a date-only string to different formats.
 * Date-only strings represent calendar dates (not moments in time),
 * so they should NOT be converted between timezones.
 *
 * @param {string} dateString - Date string in YYYY-MM-DD or M-D-YYYY format
 * @param {string} format - Output format: 'display', 'long', 'iso'
 * @returns {string|null} - Formatted string or null if invalid
 */
export const formatDateOnly = (dateString, format = 'display') => {
  if (!dateString) return null;

  let year, month, day;

  // Check if it's M-D-YYYY or MM-DD-YYYY format
  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString)) {
    const parts = dateString.split('-');
    month = parseInt(parts[0], 10) - 1; // JS months are 0-indexed
    day = parseInt(parts[1], 10);
    year = parseInt(parts[2], 10);
  }
  // Check if it's YYYY-MM-DD format
  else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const parts = dateString.split('-');
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
    day = parseInt(parts[2], 10);
  }
  // Invalid format
  else {
    return dateString;
  }

  // Create date at noon to avoid any DST issues
  const date = new Date(year, month, day, 12, 0, 0);
  if (isNaN(date.getTime())) {
    console.warn('Invalid date-only string:', dateString);
    return dateString;
  }

  switch (format) {
    case 'display':
      // Standard format: DD/MM/YYYY
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    case 'long':
      // Long format: 15 de enero de 2024
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    case 'iso':
      // ISO format: YYYY-MM-DD
      return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    default:
      return dateString;
  }
};

/**
 * Convert a UTC datetime string to a local timezone display string.
 *
 * @param {string} utcDateString - ISO datetime string (e.g., "2024-01-15T10:30:00Z")
 * @param {string} format - Output format: 'iso', 'display', 'displayWithTZ', 'date', 'time'
 * @returns {string|null} - Formatted string or null if invalid
 */
export const convertUTCToLocal = (utcDateString, format = 'iso') => {
  if (!utcDateString) return null;

  // Ensure the timestamp is interpreted as UTC
  let utcString = utcDateString;
  // Check if it has timezone info at the end (Z, +HH:MM, -HH:MM)
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(utcDateString);
  if (!hasTimezone) {
    utcString = utcDateString + 'Z';
  }

  // Validate the date string
  const date = new Date(utcString);
  if (isNaN(date.getTime())) {
    console.warn('Invalid date string:', utcDateString);
    return null;
  }

  // Return different formats based on needs
  switch (format) {
    case 'iso':
      // Return UTC string with proper timezone indicator
      return utcString;
    case 'display':
      // Return formatted string for direct display (local timezone)
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    case 'displayWithTZ':
      // Return formatted string with timezone indicator
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      });
    case 'date':
      // Return just the date part in local timezone
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    case 'time':
      // Return just the time part in local timezone
      return date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    default:
      return utcDateString;
  }
};

/**
 * Convert a local date/time to UTC ISO string for sending to backend.
 *
 * @param {string|Date} localDateTime - Local date/time string or Date object
 * @returns {string|null} - ISO UTC string or null if invalid
 */
export const convertLocalToUTC = (localDateTime) => {
  if (!localDateTime) return null;
  const localDate = new Date(localDateTime);
  if (isNaN(localDate.getTime())) return null;
  return localDate.toISOString();
};

/**
 * Convert local date (YYYY-MM-DD) to UTC date string for backend filtering.
 *
 * @param {string} localDateString - Date string in YYYY-MM-DD format
 * @param {boolean} isEndDate - If true, uses end of day (23:59:59.999)
 * @returns {string|null} - ISO UTC string or null if invalid
 */
export const convertLocalDateToUTC = (localDateString, isEndDate = false) => {
  if (!localDateString) return null;

  // Parse the date string as local time by adding time component
  const timeStr = isEndDate ? 'T23:59:59.999' : 'T00:00:00.000';
  const localDateTimeStr = localDateString + timeStr;

  const localDate = new Date(localDateTimeStr);
  if (isNaN(localDate.getTime())) return null;

  return localDate.toISOString();
};

/**
 * Format a Date object as YYYY-MM-DD in local time.
 */
export const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Check if a string looks like an ISO datetime (with time component).
 * Returns false for date-only strings (YYYY-MM-DD) which should be kept as-is.
 */
const isISODateTimeWithTime = (str) => {
  if (typeof str !== 'string') return false;
  // Match ISO datetime with time component: YYYY-MM-DDTHH:mm or similar
  // But NOT date-only: YYYY-MM-DD
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
};

/**
 * Process outgoing data to convert local datetime strings to UTC for backend.
 *
 * This should be called before sending data to the backend when:
 * - User enters dates/times in forms (which are in local time)
 * - Data contains datetime fields that need to be stored as UTC
 *
 * Rules:
 * - Date-only strings (YYYY-MM-DD, DD/MM/YYYY, etc.) are kept as-is
 *   (they represent calendar dates, not moments in time)
 * - ISO datetime strings with time component (YYYY-MM-DDTHH:mm) are converted to UTC
 *
 * @param {object} data - Object with potential datetime fields
 * @returns {object} - Processed data with datetime strings converted to UTC
 */
export const processOutgoingDateFields = (data) => {
  if (!data || typeof data !== 'object') return data;

  const result = Array.isArray(data) ? [] : {};

  for (const [key, value] of Object.entries(data)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively process nested objects
      result[key] = processOutgoingDateFields(value);
    } else if (Array.isArray(value)) {
      // Recursively process arrays
      result[key] = value.map(item =>
        typeof item === 'object' ? processOutgoingDateFields(item) : item
      );
    } else if (isISODateTimeWithTime(value)) {
      // Convert local ISO datetime to UTC
      const localDate = new Date(value);
      if (!isNaN(localDate.getTime())) {
        result[key] = localDate.toISOString();
      } else {
        result[key] = value;
      }
    } else {
      // Keep as-is (including date-only strings like YYYY-MM-DD)
      result[key] = value;
    }
  }

  return result;
};

/**
 * Recursively process an object/array and add display versions for datetime fields.
 *
 * For each datetime field found:
 * - Keeps the original value unchanged
 * - Adds `{field}_utc` with normalized UTC string (Z suffix ensured) - for datetime only
 * - Adds `{field}_display` with localized display string
 *
 * Handles both:
 * - ISO datetime strings (YYYY-MM-DDTHH:mm:ss) - converted from UTC to local timezone
 * - ISO date-only strings (YYYY-MM-DD) - formatted as calendar dates (no timezone conversion)
 *
 * Common datetime field names that will be processed:
 * - created_at, updated_at, deleted_at
 * - sent_at, opened_at, clicked_at, bounced_at
 * - scheduled_at, executed_at, completed_at
 * - last_run_at, last_sync_at
 * - date, timestamp
 * - Any field ending with _at, _date, _time
 *
 * @param {any} data - Object, array, or primitive to process
 * @returns {any} - Processed data with added display fields
 */
const processDateFields = (data) => {
  if (data === null || data === undefined) {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => processDateFields(item));
  }

  // Handle objects
  if (typeof data === 'object' && data !== null) {
    const processed = {};

    for (const [key, value] of Object.entries(data)) {
      // Recursively process nested objects/arrays
      processed[key] = processDateFields(value);

      // Check if this looks like a datetime string (with time component)
      if (isISODateTimeString(value)) {
        // Add UTC-normalized version (ensures Z suffix)
        processed[`${key}_utc`] = convertUTCToLocal(value, 'iso');
        // Add human-readable display version with timezone
        processed[`${key}_display`] = convertUTCToLocal(value, 'displayWithTZ');
      }
      // Check if this looks like a date-only string (YYYY-MM-DD)
      else if (isISODateOnlyString(value)) {
        // Date-only strings are calendar dates, not moments in time
        // Format them without timezone conversion
        processed[`${key}_display`] = formatDateOnly(value, 'display');
      }
    }

    return processed;
  }

  // Return primitives unchanged
  return data;
};

/**
 * Process API response data to add display versions for datetime fields.
 * This is called automatically on all API responses.
 */
const processApiResponse = (data) => {
  return processDateFields(data);
};

// ============================================================================
// API REQUEST FUNCTIONS
// ============================================================================

// Generic function to make an authenticated request.
// Token attachment and 401 refresh/retry are handled by apiClient interceptors.
// The `refreshToken` param is kept for backward compat but no longer used.
const makeAuthenticatedRequest = async (endpoint, _refreshToken, options = {}) => {
  const {
    method = 'get',
    data = null,
    headers = {},
    responseType = null,
    withCredentials = false,
  } = options;

  const response = await apiClient({
    method,
    url: endpoint,
    data,
    headers,
    responseType,
    withCredentials,
  });

  if (response.data && responseType !== 'blob') {
    response.data = processApiResponse(response.data);
  }
  return response;
};

// Generic function to make a public request (no authentication required)
// Used for endpoints like registration, password reset, etc.
export const makePublicRequest = async (endpoint, options = {}) => {
  const {
    method = 'get',
    data = null,
    headers = {},
    responseType = null,
    withCredentials = false
  } = options;

  const axiosConfig = {
    method,
    url: `${backendUrl}/${endpoint}`,
    data,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...headers,
    },
    responseType,
    withCredentials
  };

  try {
    const response = await axios(axiosConfig);
    // Process datetime fields in response data (skip for blob responses)
    if (response.data && responseType !== 'blob') {
      response.data = processApiResponse(response.data);
    }
    return response;
  } catch (error) {
    console.error('Public request failed:', error);
    throw error;
  }
};

export default makeAuthenticatedRequest;
